import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { ProdutosLista } from "@/components/ProdutosLista";
import { SeloDestaque } from "@/components/SeloDestaque";
import { ChatServico } from "@/components/ChatServico";
import { TratativaImpedimento, SolicitacaoCancelamento, useTratativa } from "@/components/TratativaImpedimento";
import { OCORRENCIA_TIPOS_GESTAO } from "@/lib/ocorrencias";

export default function ServicoDetalhe() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isEmpresa = user?.role === "empresa";
  const isMontadora = user?.role === "montadora";
  const { servicoId } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const tratativa = useTratativa(servicoId, s);
  const [audit, setAudit] = useState([]);
  const [rastr, setRastr] = useState(null);
  const [acao, setAcao] = useState(null); // 'revisao' | 'reagendar' | 'falta' | 'pulmao'
  const [form, setForm] = useState({});
  const [pulmaoCompat, setPulmaoCompat] = useState(null);
  const [montadoras, setMontadoras] = useState([]);
  const [novaMontadora, setNovaMontadora] = useState("");
  const [causaSel, setCausaSel] = useState(null);
  const [videoAberto, setVideoAberto] = useState(null);

  const classificarCausa = async () => {
    try {
      const r = await api.post(`/servicos/${servicoId}/classificar-causa`, { causa: causaSel });
      toast.success(r.data.atribuivel_ao_montador
        ? "Causa classificada — atribuível ao montador (impacta qualidade)"
        : "Causa classificada — NÃO atribuível (montador protegido)");
      setCausaSel(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = () => {
    api.get(`/servicos/${servicoId}`).then(r => setS(r.data));
    api.get(`/servicos/${servicoId}/rastreamento`).then(r => setRastr(r.data)).catch(() => {});
    if (isAdmin) api.get(`/dashboard/auditoria?entity=servico&entity_id=${servicoId}`).then(r => setAudit(r.data)).catch(() => {});
    if (isAdmin) api.get("/montadoras").then(r => setMontadoras(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, [servicoId]);

  const mudarStatusManual = async (novoStatus) => {
    const motivo = window.prompt(`Mudar status para "${novoStatus.replaceAll("_", " ")}".\nInforme o motivo (obrigatório, fica auditado):`);
    if (!motivo || !motivo.trim()) { toast.error("Motivo é obrigatório"); return; }
    try {
      await api.put(`/admin/servicos/${servicoId}/status`, { status: novoStatus, motivo });
      toast.success("Status alterado manualmente (auditado)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const atribuirMontadora = async (mid) => {
    try {
      await api.put(`/servicos/${servicoId}/montadora`, { montadora_id: mid });
      toast.success(mid ? "Montadora atribuída" : "Montadora retirada");
      setNovaMontadora(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const criarRevisao = async (tipo) => {
    try {
      await api.post("/servicos/criar-revisao", { servico_original_id: servicoId, tipo, descricao: form.descricao });
      toast.success(`${tipo === "revisao" ? "Revisão" : "Assistência"} criada`);
      setAcao(null); setForm({});
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reagendar = async () => {
    try {
      await api.post(`/servicos/${servicoId}/reagendar`, { nova_data: form.nova_data, motivo: form.motivo });
      toast.success("Reagendado"); setAcao(null); setForm({}); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const registrarFalta = async () => {
    try {
      const fd = new FormData();
      fd.append("justificada", String(!!form.justificada));
      fd.append("descricao", form.descricao || "");
      await api.post(`/servicos/${servicoId}/falta`, fd);
      toast.success("Falta registrada"); setAcao(null); setForm({}); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const registrarOcorrenciaGestao = async () => {
    try {
      const r = await api.post(`/servicos/${servicoId}/ocorrencia-gestao`,
        { tipo: form.tipo, motivo: form.motivo, observacao: form.observacao });
      toast.success(r.data.status_alterado ? `Ocorrência registrada — OS agora em "${r.data.status}"` : "Ocorrência registrada (informativa)");
      setAcao(null); setForm({}); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelarOs = async () => {
    if (!form.motivo || form.motivo.trim().length < 5) { toast.error("Informe o motivo do cancelamento (mínimo 5 caracteres)"); return; }
    if (!form.confirmar) { toast.error("Confirme o cancelamento para prosseguir"); return; }
    try {
      await api.post(`/servicos/${servicoId}/cancelar`, { motivo: form.motivo, observacao: form.observacao, confirmar: true });
      toast.success("OS cancelada — permanece consultável com todo o histórico");
      setAcao(null); setForm({}); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const solicitarCancelamento = async () => {
    try {
      await api.post(`/servicos/${servicoId}/solicitar-cancelamento`, { motivo: form.motivo, observacao: form.observacao });
      toast.success("Solicitação enviada — aguardando decisão da Empresa");
      setAcao(null); setForm({}); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const abrirAcao = (a) => {
    const mapa = { reaproveitar_data: null, reagendar: "reagendar", falta: "falta", revisao: "revisao",
      assistencia: "assistencia", cancelar: "cancelar", solicitar_cancelamento: "solicitar_cancelamento",
      ocorrencia_gestao: "ocorrencia_gestao" };
    if (mapa[a]) { setForm({}); setAcao(mapa[a]); }
  };
  const podeAcao = (a) => (tratativa?.acoes || []).includes(a);
  const osAberta = !["finalizado", "cancelado", "DEMO_ENCERRADA"].includes(s?.status);
  const temNaoDeuMontagem = (s?.ocorrencias || []).some((o) => o.tipo === "nao_deu_montagem");

  const abrirAvaliacao = async () => {
    try {
      const r = await api.post("/avaliacoes-pos-montagem", { servico_original_id: servicoId, relato_cliente: form.relato || null });
      toast.success(`Avaliação Pós-Montagem aberta (${r.data.dias_desde_montagem} dias desde a conclusão)`);
      setAcao(null); setForm({}); load();
      navigate(`/admin/avaliacoes-pos-montagem?id=${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const aprovarTroca = async () => {
    try {
      const r = await api.post(`/servicos/${servicoId}/aprovar-troca-total`, { motivo: form.motivo, observacao: form.observacao, quantidade_volumes: form.volumes ? Number(form.volumes) : null });
      toast.success(`Troca Total aprovada — OS encerrada · Pulmão: ${r.data.pulmao}`);
      setAcao(null); setForm({}); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const buscarPulmao = async () => {
    try {
      const r = await api.get(`/pulmao/cruzar/${servicoId}`);
      setPulmaoCompat(r.data); setAcao("pulmao");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const baixarRelatorioFinal = async () => {
    try {
      const r = await api.get(`/servicos/${servicoId}/relatorio-final.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `relatorio_final_${s?.pim_code || servicoId}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!s) return <div className="p-12 text-muted-foreground">Carregando…</div>;

  return (
    <div className="p-8 max-w-6xl">
      <Link to="/admin/servicos" className="text-sm hover:underline">&larr; Voltar</Link>
      <div className="flex justify-between items-end mb-8 mt-2">
        <div>
          <div className="label-cap">{s.tipo}{s.retorno_garantia && " · RETORNO GARANTIA"}</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">{s.pim_code}</h1>
        </div>
        <div className="text-right">
          <div className="label-cap">Status</div>
          <div className="font-heading font-bold text-xl capitalize">{(s.status || "").replaceAll("_", " ")}</div>
          {s.status === "finalizado" && (
            <Button size="sm" variant="outline"
              className="mt-2 h-8 text-xs border-[#0A2540] text-[#0A2540] hover:bg-slate-50 font-bold"
              onClick={baixarRelatorioFinal} data-testid="btn-relatorio-final">
              📄 Relatório Final (PDF)
            </Button>
          )}
          {(user?.role === "empresa" || user?.role === "montadora") && !s.montador_id &&
            ["aguardando_disponibilidade", "aguardando_aceite_tecnico", "em_microjanela",
              "aguardando_distribuicao"].includes(s.status) && (
            <Button size="sm" variant="outline"
              className="mt-2 h-8 text-xs border-[#FF6700] text-[#FF6700] hover:bg-orange-50 font-bold"
              onClick={async () => {
                try {
                  await api.post(`/servicos/${servicoId}/redistribuir`);
                  toast.success("Oferta redistribuída — os montadores elegíveis foram notificados");
                  load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              data-testid="btn-redistribuir">
              🔄 Redistribuir oferta agora
            </Button>
          )}
          {isAdmin && (
            <Select value="" onValueChange={(v) => mudarStatusManual(v)}>
              <SelectTrigger className="mt-2 h-8 w-56 text-xs" data-testid="admin-mudar-status">
                <SelectValue placeholder="Mudar status manualmente…" />
              </SelectTrigger>
              <SelectContent>
                {["aguardando_distribuicao", "aguardando_disponibilidade", "aguardando_aceite_tecnico",
                  "aguardando_data_tecnico", "aguardando_confirmacao_cliente", "agendado",
                  "em_atendimento", "finalizado", "aguardando_ordem", "ausente", "impedido", "cancelada"]
                  .filter((st) => st !== s.status)
                  .map((st) => (
                    <SelectItem key={st} value={st}>{st.replaceAll("_", " ")}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="mb-6 grid lg:grid-cols-2 gap-4" data-testid="admin-chat-servico">
        <ChatServico meuTipo={user?.role === "empresa" || user?.role === "montadora" ? "central" : user?.role}
          titulo="Canal externo (o cliente vê)"
          somenteLeitura={["finalizado", "cancelado", "DEMO_ENCERRADA"].includes(s?.status)}
          listar={async () => (await api.get(`/servicos/${servicoId}/chat`, { params: { canal: "externo" } })).data}
          enviar={async (t) => api.post(`/servicos/${servicoId}/chat`, { texto: t, canal: "externo" })} />
        <ChatServico meuTipo={user?.role}
          titulo="Canal interno (montador × central — invisível ao cliente)"
          listar={async () => (await api.get(`/servicos/${servicoId}/chat`, { params: { canal: "interno" } })).data}
          enviar={async (t) => api.post(`/servicos/${servicoId}/chat`, { texto: t, canal: "interno" })} />
      </div>

      {/* Classificação de causa (Score definitivo — proteção contra penalização injusta) */}
      {isAdmin && (s.encerramento_tipo === "com_assistencia" || s.gerou_garantia || s.tipo === "assistencia" || s.tipo === "garantia") && (
        <div className="border border-border rounded-lg p-4 mb-6 bg-[#FFF7ED]" data-testid="classificar-causa-bloco">
          <div className="label-cap mb-1">Classificação de causa (obrigatória antes de penalizar o montador)</div>
          {s.causa_classificacao ? (
            <div className="text-sm">
              Causa: <b className="capitalize">{s.causa_classificacao.replaceAll("_", " ")}</b>
              {s.causa_observacao && <span className="text-muted-foreground"> · {s.causa_observacao}</span>}
              <Button size="sm" variant="ghost" className="ml-2 h-7 text-xs" onClick={() => setCausaSel("")}
                data-testid="causa-reclassificar">Reclassificar</Button>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">Sem classificação — este retrabalho <b>não</b> penaliza o score até ser classificado.</div>
          )}
          {(causaSel !== null || !s.causa_classificacao) && (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <select value={causaSel || ""} onChange={e => setCausaSel(e.target.value)}
                className="border border-border rounded-md text-sm px-3 py-2 bg-white" data-testid="causa-select">
                <option value="">Selecionar causa…</option>
                <optgroup label="NÃO atribuível ao montador (não penaliza)">
                  <option value="defeito_fabricacao">Defeito de fabricação</option>
                  <option value="peca_faltante">Peça faltante</option>
                  <option value="avaria_previa">Avaria prévia</option>
                  <option value="problema_cliente">Problema do cliente</option>
                  <option value="problema_terceiro">Problema de terceiro</option>
                  <option value="problema_expedicao_transporte">Expedição / transporte</option>
                </optgroup>
                <optgroup label="Atribuível ao montador (penaliza qualidade)">
                  <option value="erro_montagem">Erro de montagem</option>
                  <option value="dano_execucao">Dano na execução</option>
                  <option value="conduta_inadequada">Conduta inadequada</option>
                  <option value="outra_atribuivel">Outra atribuível</option>
                </optgroup>
              </select>
              <Button size="sm" className="btn-sharp brand-bg text-white" disabled={!causaSel}
                onClick={classificarCausa} data-testid="causa-salvar">Classificar</Button>
            </div>
          )}
        </div>
      )}

      {/* Ações administrativas */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-border pb-6">
        {(s.status === "finalizado" || (s.status === "impedido" && podeAcao("revisao"))) && (
          <>
            <Button onClick={() => setAcao("revisao")} variant="outline" className="btn-sharp" data-testid="acao-revisao">+ Criar Revisão</Button>
            <Button onClick={() => setAcao("assistencia")} variant="outline" className="btn-sharp" data-testid="acao-assistencia">+ Criar Assistência</Button>
          </>
        )}
        {s.data_confirmada && osAberta && (
          <Button onClick={() => setAcao("reagendar")} variant="outline" className="btn-sharp" data-testid="acao-reagendar">Reagendar</Button>
        )}
        {s.montador_id && osAberta && (
          <Button onClick={() => setAcao("falta")} variant="outline" className="btn-sharp" data-testid="acao-falta">
            Registrar Falta
          </Button>
        )}
        {s.status === "finalizado" && (isEmpresa || isMontadora || isAdmin) && !["aguardando_questionario","aguardando_evidencias","aguardando_avaliacao_humana","aguardando_complemento"].includes(s.avaliacao_pos_montagem?.status) && (
          <Button onClick={() => { setForm({}); setAcao("avaliacao"); }} variant="outline" className="btn-sharp border-[#0A2540]" data-testid="acao-avaliacao-pos">
            Avaliação Pós-Montagem
          </Button>
        )}
        {osAberta && temNaoDeuMontagem && (isEmpresa || isAdmin) && (
          <Button onClick={() => { setForm({}); setAcao("troca_total"); }} variant="outline" className="btn-sharp border-[#E60000] text-[#E60000]" data-testid="acao-aprovar-troca">
            Aprovar Troca Total
          </Button>
        )}
        {osAberta && (isEmpresa || isMontadora || isAdmin) && (
          <Button onClick={() => abrirAcao("ocorrencia_gestao")} variant="outline" className="btn-sharp" data-testid="acao-ocorrencia-gestao">
            Registrar ocorrência
          </Button>
        )}
        {osAberta && (isEmpresa || isAdmin) && (
          <Button onClick={() => abrirAcao("cancelar")} variant="outline" className="btn-sharp border-[#E60000] text-[#E60000] hover:bg-red-50" data-testid="acao-cancelar">
            Cancelar OS
          </Button>
        )}
        {osAberta && isMontadora && (
          <Button onClick={() => abrirAcao("solicitar_cancelamento")} variant="outline" className="btn-sharp border-[#E60000] text-[#E60000] hover:bg-red-50" data-testid="acao-solicitar-cancelamento">
            Solicitar cancelamento
          </Button>
        )}
        {s.tipo === "assistencia" && (
          <Button onClick={buscarPulmao} variant="outline" className="btn-sharp" data-testid="acao-pulmao">
            Buscar no Pulmão
          </Button>
        )}
      </div>

      {s.status === "cancelado" && (
        <div className="border-2 border-[#E60000] bg-red-50 p-5 mb-6" data-testid="os-cancelada-bloco">
          <div className="label-cap">OS cancelada</div>
          <div className="text-sm mt-1">Motivo: <b>{s.cancelado_motivo || "—"}</b></div>
          {s.cancelado_observacao && <div className="text-sm">Observação: {s.cancelado_observacao}</div>}
          <div className="text-[11px] text-muted-foreground mt-1">
            {s.cancelado_por} ({s.cancelado_perfil}) · {s.cancelado_em && new Date(s.cancelado_em).toLocaleString("pt-BR")}
            {s.status_anterior_cancelamento && ` · status anterior: ${s.status_anterior_cancelamento}`}
          </div>
        </div>
      )}

      {s.avaliacao_pos_montagem && (
        <Link to={`/admin/avaliacoes-pos-montagem?id=${s.avaliacao_pos_montagem.id}`} className="block border border-[#0A2540] bg-white p-3 mb-4 text-sm hover:bg-slate-50" data-testid="os-avaliacao-indicador">
          <span className="label-cap">Avaliação Pós-Montagem</span>{" "}
          <b>{s.avaliacao_pos_montagem.status === "avaliada" ? "concluída" : "pendente"}</b>
          {s.avaliacao_pos_montagem.conclusao && ` · conclusão: ${s.avaliacao_pos_montagem.conclusao} → ${s.avaliacao_pos_montagem.destino}`}
          <span className="text-muted-foreground"> · o status da OS não é alterado pela avaliação</span>
        </Link>
      )}
      {tratativa?.encaminhada_para && (
        <div className="border-2 border-[#0A2540] bg-slate-50 p-4 mb-4" data-testid="os-encaminhada">
          <div className="label-cap">Tratativa</div>
          <div className="font-heading font-bold">Encaminhada para {tratativa.encaminhada_para}</div>
          <div className="text-xs text-muted-foreground">OS filha <Link className="underline font-mono" to={`/admin/servicos/${tratativa.filha_ativa.id}`}>{tratativa.filha_ativa.pim_code}</Link> ({tratativa.filha_ativa.status}) é o atendimento operacional ativo. Esta OS permanece preservada em "{s.status}".</div>
        </div>
      )}
      <SolicitacaoCancelamento servicoId={servicoId} tratativa={tratativa} onSaved={load} />
      <TratativaImpedimento servicoId={servicoId} s={s} tratativa={tratativa} onAcao={abrirAcao} onSaved={load} />

      {/* Montadora gestora (rodízio ponderado / manual ADM) */}
      {(isAdmin || s.montadora_id) && (
        <div className="border border-border bg-white p-5 mb-6" data-testid="montadora-gestora">
          <div className="label-cap mb-2">Montadora Gestora</div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-semibold" data-testid="montadora-gestora-nome">
              {s.montadora_nome || montadoras.find(m => m.id === s.montadora_id)?.nome || s.montadora?.nome || s.montadora_id || "— nenhuma —"}
            </span>
            {s.montadora_atribuicao && (
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-[#F4F4F5]">
                {s.montadora_atribuicao === "rodizio_ponderado" ? "Rodízio ponderado"
                  : s.montadora_atribuicao === "manual_admin" ? "Atribuição manual"
                  : "Vínculo do montador"}
              </span>
            )}
            {isAdmin && (
              <>
                <Select value={novaMontadora} onValueChange={setNovaMontadora}>
                  <SelectTrigger className="w-64" data-testid="montadora-gestora-select">
                    <SelectValue placeholder="Escolher montadora..." />
                  </SelectTrigger>
                  <SelectContent>
                    {montadoras.map(m => <SelectItem key={m.id} value={m.id}>{m.nome || m.razao_social}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" className="btn-sharp" disabled={!novaMontadora}
                  onClick={() => atribuirMontadora(novaMontadora)} data-testid="montadora-gestora-atribuir">
                  Atribuir
                </Button>
                {s.montadora_id && (
                  <Button variant="outline" className="btn-sharp text-[#E60000] border-[#E60000]"
                    onClick={() => atribuirMontadora(null)} data-testid="montadora-gestora-retirar">
                    Retirar
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Rastreamento da distribuição */}
      {rastr && (
        <div className="border border-border bg-white p-5 mb-6" data-testid="rastreamento">
          <div className="label-cap mb-3">🔎 Rastreamento da Distribuição</div>
          {rastr.distribuido_em ? (
            <div className="text-xs text-muted-foreground mb-3">
              Distribuído aos montadores da região em {new Date(rastr.distribuido_em).toLocaleString("pt-BR")}
              {rastr.sem_candidatos && <span className="text-[#E60000] font-semibold"> · nenhum montador na região</span>}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground mb-3">Ainda não distribuído aos montadores.</div>
          )}
          {rastr.candidatos.length > 0 && (
            <div className="space-y-1.5 mb-4">
              {rastr.candidatos.map(c => (
                <div key={c.montador_id} className="flex flex-wrap items-center gap-2 text-sm border border-border p-2"
                  data-testid={`rastr-cand-${c.montador_id}`}>
                  <span className="font-medium">{c.nome}</span>
                  <SeloDestaque show={c.destaque} />
                  {c.distancia_km != null && <span className="text-[11px] text-muted-foreground">{Number(c.distancia_km).toFixed(1)} km</span>}
                  {c.aceitou ? (
                    <span className="px-2 py-0.5 text-[10px] uppercase bg-[#00A859] text-white">✓ Aceitou{rastr.aceito?.em && ` · ${new Date(rastr.aceito.em).toLocaleString("pt-BR")}`}</span>
                  ) : c.recusa ? (
                    <span className="px-2 py-0.5 text-[10px] uppercase bg-[#E60000] text-white" title={c.recusa.motivo || ""}>✗ Recusou · {new Date(c.recusa.em).toLocaleDateString("pt-BR")}</span>
                  ) : (
                    <span className="px-2 py-0.5 text-[10px] uppercase bg-[#F4F4F5]">Notificado · aguardando</span>
                  )}
                  {c.recusa?.motivo && <span className="text-[11px] text-muted-foreground">“{c.recusa.motivo}”</span>}
                </div>
              ))}
            </div>
          )}
          {rastr.eliminados_ultima_distribuicao?.lista?.length > 0 && (
            <div className="mb-4" data-testid="rastr-eliminados">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#E60000] mb-1.5">
                Não receberam a oferta (última distribuição
                {rastr.eliminados_ultima_distribuicao.em && ` · ${new Date(rastr.eliminados_ultima_distribuicao.em).toLocaleString("pt-BR")}`})
              </div>
              <div className="space-y-1">
                {rastr.eliminados_ultima_distribuicao.lista.map((e, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 text-xs border border-dashed border-[#E60000]/40 bg-[#FFF5F5] p-2"
                    data-testid={`rastr-elim-${i}`}>
                    <span className="font-medium">{e.nome}</span>
                    <span className="px-1.5 py-0.5 text-[10px] uppercase bg-[#E60000]/10 text-[#E60000] font-semibold">
                      {{ sem_cnpj_ativo: "Empresa exige NF — sem CNPJ ativo",
                         fora_do_raio: "Fora do raio de 30 km",
                         condicoes_recusadas: "Recusou as condições da empresa",
                         ja_ofertado_anteriormente: "Já recebeu esta oferta antes",
                         bairro_nao_atendido: "Bairro não atendido",
                         regiao_incompativel: "Região incompatível" }[e.motivo] || e.motivo}
                    </span>
                    {e.detalhe && <span className="text-muted-foreground">{e.detalhe}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-3 text-xs">
            <div className="border-l-2 border-[#142766] pl-2">
              <div className="label-cap">Agenda</div>
              {rastr.agenda.datas_cliente.length > 0 && <div>Datas do cliente: {rastr.agenda.datas_cliente.join(", ")}</div>}
              {rastr.agenda.data_proposta_tecnico && <div>Proposta do técnico: {rastr.agenda.data_proposta_tecnico}</div>}
              <div>Confirmada: <strong>{rastr.agenda.data_confirmada || "—"}</strong></div>
            </div>
            <div className="border-l-2 border-[#00A859] pl-2">
              <div className="label-cap">Execução</div>
              <div>Check-in: {rastr.execucao.checkin?.at ? new Date(rastr.execucao.checkin.at).toLocaleString("pt-BR") : "—"}</div>
              <div>Finalizado: {rastr.execucao.concluido_em ? new Date(rastr.execucao.concluido_em).toLocaleString("pt-BR") : "—"}</div>
            </div>
            <div className="border-l-2 border-[#FFCC00] pl-2">
              <div className="label-cap">Linha do tempo</div>
              {rastr.timeline.slice(-4).map((t, i) => (
                <div key={i}>{new Date(t.em).toLocaleDateString("pt-BR")} · {t.evento}</div>
              ))}
              {rastr.timeline.length === 0 && <div>—</div>}
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title="Cliente">
            <KV k="Nome" v={s.cliente_nome} />
            <KV k="CPF" v={s.cliente_cpf} />
            <KV k="Telefone" v={s.cliente_telefone} />
            <KV k="Endereço" v={s.cliente_endereco} />
            <KV k="Cidade" v={s.cliente_cidade ? `${s.cliente_cidade}/${s.cliente_estado || ""}` : "—"} />
          </Section>
          <Section title="Produto">
            <KV k="Produto" v={s.produtos?.length > 1 ? `Pedido com ${s.produtos.length} itens` : s.produto} />
            <KV k="Quantidade" v={s.quantidade} />
            <KV k="Cor" v={s.cor || "—"} />
            <ProdutosLista produtos={s.produtos} />
          </Section>
          {s.montador && (
            <Section title="Técnico atribuído">
              <KV k="Nome" v={s.montador.nome} />
              <KV k="Telefone" v={s.montador.telefone} />
              <KV k="Score" v={s.montador.score} />
            </Section>
          )}
          {s.datas_propostas?.length > 0 && (
            <Section title="Datas propostas">
              <div className="flex gap-2 flex-wrap">
                {s.datas_propostas.map((d) => (
                  <span key={d} className={`px-3 py-2 border text-sm ${d === s.data_confirmada ? "brand-bg text-white border-transparent" : "border-border"}`}>
                    {d}
                  </span>
                ))}
              </div>
            </Section>
          )}
          {(s.checkin || s.checkout) && (
            <Section title="Check-in / Check-out">
              {s.checkin && <KV k="Check-in" v={`${new Date(s.checkin.at).toLocaleString("pt-BR")} ${s.checkin.lat ? `(${s.checkin.lat.toFixed(4)}, ${s.checkin.lng.toFixed(4)})` : ""}`} />}
              {s.checkout && <KV k="Check-out" v={`${new Date(s.checkout.at).toLocaleString("pt-BR")} ${s.checkout.lat ? `(${s.checkout.lat.toFixed(4)}, ${s.checkout.lng.toFixed(4)})` : ""}`} />}
            </Section>
          )}
          {(s.ocorrencias || []).length > 0 && (
            <Section title="Ocorrências registradas">
              {s.ocorrencias.map((oc) => (
                <div key={oc.id} className={`border-l-4 ${oc.origem === "gestao" ? "border-[#0A2540] bg-slate-50" : "border-[#E60000] bg-red-50"} p-3 mb-2 text-sm`} data-testid={`oc-${oc.id}`}>
                  <div className="font-semibold">{oc.tipo_label}{oc.origem === "gestao" && <span className="ml-2 text-[10px] uppercase font-bold px-1.5 py-0.5 bg-[#0A2540] text-white">gestão · {oc.perfil}</span>}</div>
                  {oc.motivo && <div className="text-xs mt-0.5">Motivo: {oc.motivo}</div>}
                  {oc.observacao && <div className="text-xs mt-0.5">Obs.: {oc.observacao}</div>}
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {new Date(oc.em).toLocaleString("pt-BR")} · {oc.reportado_por}
                  </div>
                </div>
              ))}
              <div className="text-[11px] text-muted-foreground">As fotos da ocorrência de campo aparecem abaixo em Evidências (categoria "ocorrencia").</div>
            </Section>
          )}
          {(s.tratativas_historico || []).length > 0 && (
            <Section title="Histórico de tratativas">
              {s.tratativas_historico.map((t) => (
                <div key={t.id} className="border-l-4 border-[#F5C518] bg-[#FFFBEA] p-2 mb-1 text-xs" data-testid={`trat-${t.id}`}>
                  <b className="uppercase">{(t.acao || "").replaceAll("_", " ")}</b>
                  {t.status_anterior && ` · ${t.status_anterior} → ${t.status_novo || "agendado"}`}
                  {t.data_nova && ` · nova data ${t.data_nova}`}
                  {t.motivo && ` · ${t.motivo}`}
                  <div className="text-[11px] text-muted-foreground">{t.por} ({t.perfil}) · {new Date(t.em).toLocaleString("pt-BR")}</div>
                </div>
              ))}
            </Section>
          )}
          {(s.checklist_antes || s.checklist_depois) && (
            <Section title="Checklists (pins baixados)">
              {s.checklist_antes && (
                <KV k="Antes da montagem" v={`Cliente ${s.checklist_antes.nome_cliente_confirmado ? "✓" : "—"} · Produto ${s.checklist_antes.produto_confirmado ? "✓" : "—"} · Cor ${s.checklist_antes.cor_confirmada ? "✓" : "—"} · Volumes: ${s.checklist_antes.quantidade_volumes ?? "—"}`} />
              )}
              {s.checklist_depois && <KV k="Depois da montagem" v={s.checklist_depois.descricao || "Concluído"} />}
            </Section>
          )}
          {(s.assinatura_cliente || s.assinatura_tecnico) && (
            <Section title="Ficha assinada">
              <div className="grid grid-cols-2 gap-3">
                {s.assinatura_cliente && (
                  <div>
                    <div className="label-cap mb-1">Cliente</div>
                    <img src={s.assinatura_cliente} alt="Assinatura do cliente" className="border border-border bg-white w-full" data-testid="assinatura-cliente" />
                  </div>
                )}
                {s.assinatura_tecnico && (
                  <div>
                    <div className="label-cap mb-1">Técnico</div>
                    <img src={s.assinatura_tecnico} alt="Assinatura do técnico" className="border border-border bg-white w-full" data-testid="assinatura-tecnico" />
                  </div>
                )}
              </div>
            </Section>
          )}
          <Section title="Evidências">
            {(s.evidencias || []).length === 0 ? (
              <div className="text-sm text-muted-foreground">Nenhuma evidência enviada.</div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {s.evidencias.map((ev) => {
                  const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${ev.servico_id}/${ev.categoria}/${(ev.path || "").split("/").pop()}?s=${ev.sig || ""}`;
                  return (
                  <div key={ev.id} className="border border-border p-2 text-xs">
                    <div className="label-cap text-[10px]">{ev.categoria}</div>
                    {ev.content_type?.startsWith("image/") ? (
                      <img src={fileUrl} alt={ev.filename} className="mt-1 aspect-square object-cover" />
                    ) : ev.content_type?.startsWith("video/") ? (
                      <button type="button" onClick={() => setVideoAberto({ ...ev, url: fileUrl })}
                        className="mt-1 w-full aspect-square bg-[#0A2540] text-white flex flex-col items-center justify-center gap-1 hover:bg-[#123a63] transition-colors"
                        data-testid={`video-card-${ev.id}`}>
                        <span className="text-2xl leading-none">▶</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider">Vídeo — assistir</span>
                      </button>
                    ) : (
                      <div className="aspect-square bg-secondary flex items-center justify-center text-muted-foreground">arquivo</div>
                    )}
                    <div className="mt-1 truncate">{ev.filename}</div>
                  </div>
                  );
                })}
              </div>
            )}
          </Section>
        </div>

        <div className="space-y-6">
          {isAdmin && (
          <div className="border border-border bg-white p-5">
            <div className="label-cap mb-3">Auditoria (PIM)</div>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {audit.length === 0 && <div className="text-xs text-muted-foreground">Sem eventos.</div>}
              {audit.map((a) => (
                <div key={a.id} className="border-l-2 border-border pl-3 text-xs" data-testid={`audit-${a.id}`}>
                  <div className="font-mono text-[10px] text-muted-foreground">{new Date(a.timestamp).toLocaleString("pt-BR")}</div>
                  <div className="font-medium">{a.action}</div>
                  <div className="text-muted-foreground">{a.user_email || "sistema"} · {a.ip || "—"}</div>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <Dialog open={acao === "revisao" || acao === "assistencia"} onOpenChange={() => setAcao(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{acao === "revisao" ? "Criar Revisão" : "Criar Assistência"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground border border-border p-3 bg-[#F4F4F5]">
              Se for revisão dentro de 30 dias do checkout, voltará para o mesmo técnico SEM pagamento adicional (retorno de garantia).
              Após 31 dias, segue tabela financeira normal.
            </div>
            <div>
              <Label className="label-cap">Descrição</Label>
              <Textarea value={form.descricao || ""} onChange={(e) => setForm({ ...form, descricao: e.target.value })} className="mt-2" data-testid="rev-desc" />
            </div>
            <Button onClick={() => criarRevisao(acao)} className="w-full btn-sharp brand-bg text-white" data-testid="rev-criar">Criar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={acao === "avaliacao"} onOpenChange={() => setAcao(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Avaliação Pós-Montagem — {s.pim_code}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground border border-border p-3 bg-[#F4F4F5]">
              Registra a reclamação do cliente como Avaliação (desacoplada da OS). Nenhuma Revisão, Assistência ou Troca é criada automaticamente — a decisão é humana após triagem e evidências.
            </div>
            <div>
              <Label className="label-cap">Relato do cliente (opcional)</Label>
              <Textarea value={form.relato || ""} onChange={(e) => setForm({ ...form, relato: e.target.value })} className="mt-2" data-testid="aval-relato" />
            </div>
            <Button onClick={abrirAvaliacao} className="w-full btn-sharp brand-bg text-white" data-testid="aval-abrir-confirmar">Abrir avaliação</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={acao === "troca_total"} onOpenChange={() => setAcao(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Aprovar Troca Total — {s.pim_code}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs border border-[#E60000] p-3 bg-red-50">
              Exceção controlada: só disponível porque existe ocorrência formal "Produto não deu montagem". A OS é encerrada como troca_total e o produto segue para o Pulmão pela regra atual (Empresa: imediato · Cliente Particular: após recolhimento).
            </div>
            <div><Label className="label-cap">Motivo (obrigatório)</Label><Textarea value={form.motivo || ""} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className="mt-2" data-testid="troca-motivo" /></div>
            <div><Label className="label-cap">Volumes (opcional)</Label><Input type="number" value={form.volumes || ""} onChange={(e) => setForm({ ...form, volumes: e.target.value })} className="mt-2" data-testid="troca-volumes" /></div>
            <Button onClick={aprovarTroca} className="w-full btn-sharp bg-[#E60000] text-white hover:bg-[#c00000]" data-testid="troca-confirmar">Aprovar Troca Total</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={acao === "ocorrencia_gestao"} onOpenChange={() => setAcao(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registrar ocorrência de gestão — {s.pim_code}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground border border-border p-3 bg-[#F4F4F5]">
              Ocorrência registrada pela gestão (sem fotos de campo). Tipos de LOGÍSTICA/GERAL colocam a OS em impedido/ausente
              para tratativa; tipos de PRODUTO abrem a contagem legal de Assistência (30 dias).
            </div>
            <div>
              <Label className="label-cap">Tipo</Label>
              <select value={form.tipo || ""} onChange={e => setForm({ ...form, tipo: e.target.value })}
                className="mt-2 w-full border border-border rounded-md text-sm px-3 py-2 bg-white" data-testid="ocg-tipo">
                <option value="">Selecionar…</option>
                {OCORRENCIA_TIPOS_GESTAO.map(([k, r, c]) => (
                  <option key={k} value={k}>{r} — {c === "produto" ? "PRODUTO" : c === "logistica" ? "LOGÍSTICA" : "GERAL"}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="label-cap">Motivo (mínimo 10 caracteres)</Label>
              <Textarea value={form.motivo || ""} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className="mt-2" data-testid="ocg-motivo" />
            </div>
            <div>
              <Label className="label-cap">Observação (opcional)</Label>
              <Textarea value={form.observacao || ""} onChange={(e) => setForm({ ...form, observacao: e.target.value })} className="mt-2" data-testid="ocg-obs" />
            </div>
            <Button onClick={registrarOcorrenciaGestao} disabled={!form.tipo} className="w-full btn-sharp brand-bg text-white" data-testid="ocg-confirmar">Registrar ocorrência</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={acao === "cancelar"} onOpenChange={() => setAcao(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancelar OS — {s.pim_code}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs border border-[#E60000] p-3 bg-red-50">
              A OS sai das abertas mas <b>não é excluída</b>: cliente, produtos, valores, evidências, montador, ocorrências e histórico ficam preservados e consultáveis.
            </div>
            <div>
              <Label className="label-cap">Motivo (obrigatório)</Label>
              <Textarea value={form.motivo || ""} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className="mt-2" data-testid="cancelar-motivo" />
            </div>
            <div>
              <Label className="label-cap">Observação (opcional)</Label>
              <Textarea value={form.observacao || ""} onChange={(e) => setForm({ ...form, observacao: e.target.value })} className="mt-2" data-testid="cancelar-obs" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.confirmar} onChange={e => setForm({ ...form, confirmar: e.target.checked })} data-testid="cancelar-confirmar-check" />
              Confirmo o cancelamento desta OS
            </label>
            <Button onClick={cancelarOs} className="w-full btn-sharp bg-[#E60000] text-white hover:bg-[#c00000]" data-testid="cancelar-confirmar">Cancelar OS</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={acao === "solicitar_cancelamento"} onOpenChange={() => setAcao(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Solicitar cancelamento — {s.pim_code}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground border border-border p-3 bg-[#F4F4F5]">
              A Montadora não cancela diretamente. A solicitação fica pendente para a Empresa aprovar, recusar ou pedir complemento.
            </div>
            <div>
              <Label className="label-cap">Motivo (obrigatório)</Label>
              <Textarea value={form.motivo || ""} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className="mt-2" data-testid="solcanc-motivo" />
            </div>
            <div>
              <Label className="label-cap">Observação (opcional)</Label>
              <Textarea value={form.observacao || ""} onChange={(e) => setForm({ ...form, observacao: e.target.value })} className="mt-2" data-testid="solcanc-obs" />
            </div>
            <Button onClick={solicitarCancelamento} className="w-full btn-sharp brand-bg text-white" data-testid="solcanc-confirmar">Enviar solicitação</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={acao === "reagendar"} onOpenChange={() => setAcao(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reagendar — {s.pim_code}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="label-cap">Nova data</Label>
              <Input type="date" value={form.nova_data || ""} onChange={(e) => setForm({ ...form, nova_data: e.target.value })} className="mt-2" data-testid="rea-data" />
            </div>
            <div>
              <Label className="label-cap">Motivo</Label>
              <Textarea value={form.motivo || ""} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className="mt-2" data-testid="rea-motivo" />
            </div>
            <Button onClick={reagendar} className="w-full btn-sharp brand-bg text-white" data-testid="rea-confirmar">Reagendar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={acao === "falta"} onOpenChange={() => setAcao(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registrar falta do técnico</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground border border-border p-3 bg-[#FFCC00]/10">
              Faltas sem justificativa: 3 em 30 dias → bloqueio automático por 7 dias. Apenas Admin pode desbloquear.
            </div>
            <div className="flex gap-2">
              <button onClick={() => setForm({ ...form, justificada: true })}
                className={`btn-sharp px-4 py-2 ${form.justificada ? "brand-bg text-white" : "border-border"}`} data-testid="falta-just-sim">
                Justificada
              </button>
              <button onClick={() => setForm({ ...form, justificada: false })}
                className={`btn-sharp px-4 py-2 ${form.justificada === false ? "bg-[#E60000] text-white" : "border-border"}`} data-testid="falta-just-nao">
                Sem justificativa
              </button>
            </div>
            <div>
              <Label className="label-cap">Descrição</Label>
              <Textarea value={form.descricao || ""} onChange={(e) => setForm({ ...form, descricao: e.target.value })} className="mt-2" data-testid="falta-desc" />
            </div>
            <Button onClick={registrarFalta} className="w-full btn-sharp brand-bg text-white" data-testid="falta-confirmar">Registrar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={acao === "pulmao"} onOpenChange={() => setAcao(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Compatibilidades no Pulmão</DialogTitle></DialogHeader>
          {!pulmaoCompat ? <div>Buscando…</div> : (
            <div>
              {pulmaoCompat.total === 0 ? (
                <div className="text-sm text-muted-foreground p-4">Nenhuma peça compatível encontrada.</div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  <div className="label-cap mb-2">{pulmaoCompat.total} peça(s) potencialmente compatível(is)</div>
                  {pulmaoCompat.compatibilidades.map(p => (
                    <div key={p.id} className="border border-border p-3 text-sm">
                      <strong>{p.peca}</strong> · {p.produto} {p.cor && `· ${p.cor}`}
                      <div className="text-xs text-muted-foreground">PIM origem: {p.pim_origem || "—"} · {p.cidade}</div>
                      <div className="text-xs">Entrada: {p.data_entrada?.slice(0,10)} · Validade: {p.validade_ate?.slice(0,10)}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-xs text-muted-foreground mt-4 p-3 bg-[#F4F4F5] border border-border">
                Apenas sinalização. A aprovação final do uso é feita na página do Pulmão.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {videoAberto && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setVideoAberto(null)} data-testid="video-modal">
          <div className="bg-white max-w-3xl w-full p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="label-cap">Vídeo da execução — {videoAberto.filename}</div>
              <button onClick={() => setVideoAberto(null)} className="text-xl leading-none px-2 hover:text-red-600"
                data-testid="video-modal-close">×</button>
            </div>
            <video src={videoAberto.url} controls autoPlay className="w-full max-h-[60vh] bg-black"
              data-testid="video-player" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" className="h-8 text-xs font-bold"
                data-testid="video-download-btn"
                onClick={async () => {
                  try {
                    const resp = await fetch(videoAberto.url);
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = videoAberto.filename || "video-execucao.mp4";
                    a.click(); URL.revokeObjectURL(url);
                  } catch { toast.error("Falha ao baixar o vídeo"); }
                }}>
                ⬇ Baixar vídeo
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-border bg-white p-5">
      <div className="label-cap mb-3">{title}</div>
      <div className="space-y-1 text-sm">{children}</div>
    </div>
  );
}
function KV({ k, v }) {
  return <div className="flex justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v}</span></div>;
}
